import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Send, Loader2, Users, PlayCircle, AlertTriangle, Brain, Trash2, Search, X, Tag, Star, AlertOctagon, ShieldAlert, FileDown, Megaphone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { API } from "@/lib/api";

// Fixed tag vocabulary — backend enforces the same list.
const TAGS = [
  { key: "vip", label: "VIP", icon: Star, bg: "bg-[#fff6e6]", fg: "text-[#b05f00]", border: "border-[#ffd59e]" },
  { key: "escalate", label: "Escalate", icon: AlertOctagon, bg: "bg-[#fff0f5]", fg: "text-[#c01974]", border: "border-[#ffb3d1]" },
  { key: "fraud-review", label: "Fraud review", icon: ShieldAlert, bg: "bg-[#fee2e2]", fg: "text-[#b91c1c]", border: "border-[#fca5a5]" },
];
const TAG_KEYS = TAGS.map((t) => t.key);

function TagPill({ tagKey, size = "xs" }) {
  const t = TAGS.find((x) => x.key === tagKey);
  if (!t) return null;
  const Icon = t.icon;
  const sz = size === "sm" ? "text-[11px] px-2 py-0.5" : "text-[9px] px-1.5 py-0";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border ${t.border} ${t.bg} ${t.fg} ${sz} font-bold`}>
      <Icon className="h-2.5 w-2.5" /> {t.label}
    </span>
  );
}

export default function WhatsAppInbox() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [memoryData, setMemoryData] = useState(null);
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState(""); // "", "vip", "escalate", "fraud-review"
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBody, setBulkBody] = useState("");
  const scrollRef = useRef(null);

  const loadConvos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wa/conversations");
      setConversations(data);
      setSelected((cur) => cur || (data.length ? data[0].phone : null));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone) => {
    if (!phone) return;
    const { data } = await api.get(`/wa/messages/${encodeURIComponent(phone)}`);
    setMessages(data);
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { loadMessages(selected); }, [selected, loadMessages]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendInvites = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wa/send-invites", {});
      setToast(`Sent ${data.sent} invites · ${data.cooldown_blocked} in cooldown · ${data.tenure_blocked} below tenure · ${data.skipped} skipped`);
      setTimeout(() => setToast(""), 5000);
      await loadConvos();
      if (selected) await loadMessages(selected);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sendInviteTo = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/wa/send-invites", { referrer_phone: selected });
      setToast(data.sent ? `Invite sent to ${selected}` : `Not sent (${data.cooldown_blocked ? "cooldown" : data.tenure_blocked ? "tenure" : "skipped"})`);
      setTimeout(() => setToast(""), 4000);
      await loadMessages(selected);
    } finally { setBusy(false); }
  };

  const broadcastSummary = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wa/daily-summary");
      setToast(`Daily summary pushed to ${data.pushed} referrers`);
      setTimeout(() => setToast(""), 4000);
      if (selected) await loadMessages(selected);
    } finally { setBusy(false); }
  };

  const sendAsAdmin = async () => {
    if (!input.trim() || !selected) return;
    setBusy(true);
    try {
      // Inject admin-sent outbound message (simulated support reply)
      await api.post("/wa/inbound", { phone: selected, text: `[ADMIN]: ${input.trim()}` });
      setInput("");
      await loadMessages(selected);
    } finally { setBusy(false); }
  };

  const openMemory = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      // Find the referrer_id for the selected phone
      const conv = conversations.find((c) => c.phone === selected);
      if (!conv?.referrer?.id) {
        setToast("No referrer found for this conversation");
        setTimeout(() => setToast(""), 3000);
        return;
      }
      const { data } = await api.get(`/ai/memory/${conv.referrer.id}`);
      setMemoryData({ ...data, referrer_name: conv.referrer?.name || selected });
      setMemoryOpen(true);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Failed to load AI memory");
      setTimeout(() => setToast(""), 3000);
    } finally { setBusy(false); }
  };

  const resetMemory = async () => {
    if (!memoryData?.referrer_id) return;
    if (!window.confirm("Reset AI memory for this referrer? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/ai/memory/${memoryData.referrer_id}/reset`);
      setToast("AI memory reset successfully");
      setTimeout(() => setToast(""), 3000);
      setMemoryOpen(false);
      setMemoryData(null);
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Failed to reset memory");
      setTimeout(() => setToast(""), 3000);
    } finally { setBusy(false); }
  };

  // Normalize query: strip whitespace, lowercase, remove '+' for phone matching.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = conversations;
    if (tagFilter) base = base.filter((c) => (c.tags || []).includes(tagFilter));
    if (!q) return base;
    const digits = q.replace(/\D/g, "");
    return base.filter((c) => {
      const name = (c.referrer?.name || "").toLowerCase();
      const phone = (c.phone || "").toLowerCase();
      const last = (c.last_body || "").toLowerCase();
      if (name.includes(q) || phone.includes(q) || last.includes(q)) return true;
      if (digits && phone.replace(/\D/g, "").includes(digits)) return true;
      return false;
    });
  }, [conversations, query, tagFilter]);

  const selectedConv = conversations.find((c) => c.phone === selected) || null;
  const selectedTags = selectedConv?.tags || [];

  // Optimistic PUT — update in memory first, reconcile on response.
  const toggleTag = async (tagKey) => {
    if (!selected) return;
    const current = new Set(selectedTags);
    if (current.has(tagKey)) current.delete(tagKey);
    else current.add(tagKey);
    const next = [...current];
    setConversations((list) => list.map((c) => c.phone === selected ? { ...c, tags: next } : c));
    try {
      await api.put(`/wa/conversations/${encodeURIComponent(selected)}/tags`,
                    { tags: next });
    } catch (e) {
      // Rollback on failure
      setConversations((list) => list.map((c) => c.phone === selected ? { ...c, tags: selectedTags } : c));
      setToast(formatApiError(e.response?.data?.detail) || "Failed to update tags");
      setTimeout(() => setToast(""), 3000);
    }
  };

  const taggedCount = tagFilter
    ? conversations.filter((c) => (c.tags || []).includes(tagFilter)).length
    : 0;

  const bulkSend = async () => {
    if (!tagFilter || !bulkBody.trim()) return;
    if (!window.confirm(`Send this message to ${taggedCount} ${tagFilter} thread(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/wa/conversations/bulk-message",
                                        { tag: tagFilter, body: bulkBody });
      setToast(`Sent to ${data.sent}${data.failed ? ` · ${data.failed} failed` : ""}`);
      setBulkBody("");
      setBulkOpen(false);
      await loadConvos();
    } catch (e) {
      setToast(formatApiError(e.response?.data?.detail) || "Bulk send failed");
    } finally {
      setBusy(false);
      setTimeout(() => setToast(""), 3500);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
            <FaWhatsapp className="h-6 w-6 text-[#25d366]" /> WhatsApp Inbox
          </h1>
          <p className="text-sm text-[#5c665a]">All simulated conversations with referrers. In production, connects to WhatsApp Business API.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="admin-btn" onClick={sendInvites} disabled={busy} data-testid="wa-send-all-invites">
            <Users className="h-4 w-4" /> Send eligible invites
          </button>
          <button className="admin-btn-pink" onClick={broadcastSummary} disabled={busy} data-testid="wa-broadcast-summary">
            <PlayCircle className="h-4 w-4" /> Broadcast daily summary
          </button>
        </div>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]" data-testid="wa-toast">{toast}</div>}

      {tagFilter && (
        <div className="admin-card flex flex-wrap items-center justify-between gap-2 border-2 border-[#e2e8e2] bg-[#fafcfa]" data-testid="wa-bulk-bar">
          <div className="text-sm">
            <span className="font-bold text-[#1a1c19]">{taggedCount}</span>{" "}
            <span className="text-[#5c665a]">thread{taggedCount === 1 ? "" : "s"} tagged</span>{" "}
            <TagPill tagKey={tagFilter} size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}/wa/conversations/export.csv?tag=${encodeURIComponent(tagFilter)}`}
              className="admin-btn-ghost"
              data-testid="wa-bulk-export"
            >
              <FileDown className="h-3.5 w-3.5" /> Export CSV
            </a>
            <button
              className="admin-btn"
              disabled={taggedCount === 0}
              onClick={() => setBulkOpen(true)}
              data-testid="wa-bulk-open"
            >
              <Megaphone className="h-3.5 w-3.5" /> Broadcast…
            </button>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setBulkOpen(false)} data-testid="wa-bulk-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="flex items-center gap-2 text-lg font-extrabold">
              <Megaphone className="h-5 w-5 text-[#ff3399]" /> Broadcast to {tagFilter}
            </h3>
            <p className="mt-1 text-xs text-[#5c665a]">
              Sends this WhatsApp message to every conversation tagged <TagPill tagKey={tagFilter} /> ({taggedCount} thread{taggedCount === 1 ? "" : "s"}).
            </p>
            <textarea
              rows={4}
              value={bulkBody}
              onChange={(e) => setBulkBody(e.target.value)}
              placeholder="Hi! Quick update from MYDAWA…"
              className="admin-input mt-3 font-mono text-sm"
              data-testid="wa-bulk-textarea"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button className="admin-btn-ghost" onClick={() => setBulkOpen(false)} data-testid="wa-bulk-cancel">Cancel</button>
              <button className="admin-btn" onClick={bulkSend} disabled={busy || !bulkBody.trim()} data-testid="wa-bulk-send">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send to {taggedCount}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="admin-card p-0">
          <div className="border-b border-[#e2e8e2] px-4 py-3">
            <h3 className="text-sm font-bold">Conversations</h3>
            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#5c665a]" />
              <input
                type="text"
                placeholder="Search name, phone or message…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="admin-input w-full !pl-9 !pr-8 py-2 text-xs"
                data-testid="wa-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-[#5c665a] hover:bg-[#fafcfa]"
                  data-testid="wa-search-clear"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {query && (
              <div className="mt-1.5 text-[10px] text-[#5c665a]" data-testid="wa-search-count">
                {filtered.length} of {conversations.length} conversations
              </div>
            )}
            {/* Tag filter pills */}
            <div className="mt-2 flex flex-wrap gap-1" data-testid="wa-tag-filter-bar">
              <button
                onClick={() => setTagFilter("")}
                className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${tagFilter === "" ? "bg-[#1a1c19] border-[#1a1c19] text-white" : "bg-white border-[#e2e8e2] text-[#5c665a]"}`}
                data-testid="wa-tag-filter-all"
              >
                All
              </button>
              {TAGS.map((t) => {
                const active = tagFilter === t.key;
                const count = conversations.filter((c) => (c.tags || []).includes(t.key)).length;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTagFilter(active ? "" : t.key)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold transition ${active ? `${t.bg} ${t.border} ${t.fg}` : "bg-white border-[#e2e8e2] text-[#5c665a]"}`}
                    data-testid={`wa-tag-filter-${t.key}`}
                  >
                    <t.icon className="h-2.5 w-2.5" /> {t.label} · {count}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="max-h-[60vh] divide-y divide-[#e2e8e2] overflow-y-auto" data-testid="wa-conv-list">
            {loading ? (
              <div className="px-4 py-6 text-center text-xs text-[#5c665a]"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#5c665a]" data-testid="wa-conv-empty">
                {query
                  ? `No conversations match "${query}".`
                  : 'No conversations yet. Click "Send eligible invites" to start.'}
              </div>
            ) : filtered.map((c) => (
              <button
                key={c.phone}
                onClick={() => setSelected(c.phone)}
                className={`block w-full px-4 py-3 text-left hover:bg-[#fafcfa] ${selected === c.phone ? "bg-[#eaf8de]" : ""}`}
                data-testid={`wa-conv-${c.phone}`}
              >
                <div className="flex items-center justify-between">
                  <div className="truncate text-sm font-semibold text-[#1a1c19]">{c.referrer?.name || c.phone}</div>
                  <div className="text-[10px] text-[#5c665a]">{c.msg_count}</div>
                </div>
                <div className="truncate text-[11px] text-[#5c665a]">{c.last_body}</div>
                {(c.tags || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-0.5" data-testid={`wa-conv-tags-${c.phone}`}>
                    {(c.tags || []).map((t) => <TagPill key={t} tagKey={t} />)}
                  </div>
                )}
                {c.referrer?.opted_out && <div className="mt-1 text-[10px] font-bold text-red-500">opted out</div>}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-card flex flex-col p-0">
          <div className="border-b border-[#e2e8e2] px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-bold">{selectedConv?.referrer?.name || selected || "—"}</span>
                  {selectedTags.map((t) => <TagPill key={t} tagKey={t} size="sm" />)}
                </div>
                <div className="text-xs text-[#5c665a]">{selected} · Simulated WhatsApp chat</div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button className="admin-btn-ghost" onClick={openMemory} disabled={!selected} data-testid="wa-memory-btn">
                  <Brain className="h-3.5 w-3.5" /> AI memory
                </button>
                <button className="admin-btn-ghost" onClick={sendInviteTo} disabled={!selected || busy} data-testid="wa-send-invite-btn">
                  <Send className="h-3.5 w-3.5" /> Send invite
                </button>
              </div>
            </div>
            {selected && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="wa-tag-toggle-bar">
                <Tag className="h-3 w-3 text-[#5c665a]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#5c665a]">Tags:</span>
                {TAGS.map((t) => {
                  const active = selectedTags.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      onClick={() => toggleTag(t.key)}
                      disabled={busy}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold transition ${active ? `${t.bg} ${t.border} ${t.fg}` : "bg-white border-[#e2e8e2] text-[#5c665a] hover:bg-[#fafcfa]"}`}
                      data-testid={`wa-tag-toggle-${t.key}`}
                      title={active ? `Remove ${t.label} tag` : `Add ${t.label} tag`}
                    >
                      <t.icon className="h-2.5 w-2.5" /> {t.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div ref={scrollRef} className="max-h-[50vh] flex-1 space-y-2 overflow-y-auto bg-[#ece5dd] p-4" data-testid="wa-chat-area">
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center rounded-xl bg-white/80 p-6 text-sm text-[#5c665a]">
                Pick a conversation or send an invite to start.
              </div>
            ) : messages.map((m) => {
              const isInbound = m.direction === "inbound";
              const isSms = m.channel === "sms";
              return (
                <div key={m.id} className={`flex ${isInbound ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    isInbound ? "bg-[#dcf8c6]" : isSms ? "bg-[#fff0d6] border border-[#e9c86a]" : "bg-white"
                  }`}>
                    {isSms && <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#a87700]">SMS · M-Pesa</div>}
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-[#5c665a]">{m.template}</div>
                    <div className="mt-0.5">{m.body}</div>
                    <div className="mt-1 text-right text-[10px] text-[#5c665a]">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 border-t border-[#e2e8e2] p-3">
            <input
              type="text"
              placeholder="Admin reply (shown as inbound in the sim)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="admin-input flex-1"
              data-testid="wa-admin-input"
            />
            <button className="admin-btn" onClick={sendAsAdmin} disabled={!selected || busy} data-testid="wa-admin-send">
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-[#e2e8e2] bg-white px-4 py-3 text-xs text-[#5c665a]">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
        All messaging is MOCKED. In production, configure WhatsApp Business API credentials to send real messages.
      </div>

      {memoryOpen && memoryData && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 px-4" onClick={() => setMemoryOpen(false)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#e2e8e2] px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-bold"><Brain className="h-5 w-5 text-[#66cc33]" /> AI memory — {memoryData.referrer_name}</h3>
              <button onClick={() => setMemoryOpen(false)} className="text-[#5c665a]">✕</button>
            </div>
            <div className="space-y-4 p-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="admin-card">
                  <div className="text-[10px] uppercase tracking-wider text-[#5c665a]">Total turns ever</div>
                  <div className="text-2xl font-extrabold">{memoryData.total_turns || 0}</div>
                </div>
                <div className="admin-card">
                  <div className="text-[10px] uppercase tracking-wider text-[#5c665a]">Kept in context</div>
                  <div className="text-2xl font-extrabold">{memoryData.turns?.length || 0}</div>
                </div>
                <div className="admin-card">
                  <div className="text-[10px] uppercase tracking-wider text-[#5c665a]">Summary length</div>
                  <div className="text-2xl font-extrabold">{(memoryData.summary || "").length}</div>
                </div>
              </div>
              {memoryData.summary && (
                <div className="rounded-xl bg-[#eaf8de] p-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#3f7c21]">Long-term summary</div>
                  <div className="mt-1 whitespace-pre-wrap text-sm text-[#1a1c19]">{memoryData.summary}</div>
                </div>
              )}
              <div className="rounded-xl border border-[#e2e8e2]" data-testid="ai-memory-turns">
                <div className="border-b border-[#e2e8e2] px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-[#5c665a]">
                  Last {memoryData.turns?.length || 0} turns sent to LLM as context
                </div>
                <div className="max-h-72 space-y-1 overflow-y-auto p-3">
                  {(memoryData.turns || []).map((t, i) => (
                    <div key={`${t.at || "t"}-${t.role}-${i}`} className={`text-xs ${t.role === "user" ? "text-[#1a1c19]" : "text-[#5c665a]"}`}>
                      <span className="font-bold">{t.role === "user" ? "User" : "Bot"}:</span> {t.text}
                    </div>
                  ))}
                </div>
              </div>
              <button className="admin-btn-danger" onClick={resetMemory} data-testid="ai-memory-reset">
                <Trash2 className="h-3.5 w-3.5" /> Reset memory
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
